import { isRecord } from '#layers/base/public';

/**
 * Canonical string key for table filters.
 *
 * `stabilizeColumnSizesDeps` re-evaluates often while scrolling/paging; keeping
 * filter serialization in a dedicated computed avoids repeated work in that hot
 * path and normalizes plain-object key order.
 */
export function getStableFilterKey(filter: unknown) {
	if (filter == null) {
		return '';
	}

	return JSON.stringify(normalizeFilterValue(filter));
}

/**
 * Recursively sorts plain-object keys while preserving array order.
 */
function normalizeFilterValue(value: unknown): unknown {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeFilterValue(entry));
	}

	if (isPlainObject(value)) {
		return Object.keys(value)
			.toSorted()
			.reduce<Record<string, unknown>>((result, key) => {
				result[key] = normalizeFilterValue(value[key]);
				return result;
			}, {});
	}

	return value;
}

/**
 * Narrow plain objects so Dates and class instances keep their native JSON
 * behavior instead of being recursively rewritten.
 */
function isPlainObject(value: unknown): value is Record<string, unknown> {
	if (!isRecord(value)) {
		return false;
	}

	const prototype = Object.getPrototypeOf(value);
	return prototype === Object.prototype || prototype === null;
}
